import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Timeline, TimelineLite, TweenLite, TimelineMax, TweenMax} from "gsap";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {DragonTribe} from "../../DragonTribe";

export interface IBoxConfig {
    backgroundTexture?:PIXI.Texture;
    title?:string;
    text?:string;
    infoSprite?:PIXI.Sprite;
    additionalInfo?:string[];
}

export class SpritePageFlipper extends BaseView {


    private _interactEnabled:boolean = false;
    private _progressBarYOffset:number = 80;
    protected _currentIndex:number = 0;
    protected _animationsInited:boolean = false;

    // Page/box elements
    protected _pageSprites:PIXI.Container [] = [];
    protected _boxWrapperSprite:PIXI.Sprite;
    protected _boxConfigs:IBoxConfig [] = [];
    protected _lineDivider:PIXI.Sprite;

    // Basic next-/prev-buttons
    protected _arrowNextSprite:PIXI.Sprite;
    protected _arrowPrevSprite:PIXI.Sprite;
    protected _nextButton:PIXI.Sprite;
    protected _previousButton:PIXI.Sprite;

    // Very game specific fluff
    protected _radioButtons:PIXI.Sprite [] = [];
    protected _radioButtonWrapper:PIXI.Sprite;

    // Transitions & Timelines
    protected _pageTransitionTimeline:Timeline | null;
    protected _pageFadeOutTween:TweenLite | null;

    // Slide show
    protected _slideShowTimeline:Timeline | null;
    protected _slideShowRunning:boolean = false;
    protected _slideShowEnabled:boolean = false;

    // Texts
    protected _titles:PIXI.Text [] = [];
    protected _paragraphs:PIXI.Text [] = [];

    public get isSlideShowRunning():boolean {
        return this._slideShowRunning;
    }

    public set slideShowEnabled(value:boolean) {
        if(this._slideShowEnabled != value) {
            this._slideShowEnabled = value;
        }
        if(!this._slideShowEnabled) {
            this.stopSlideShow();
        }
    }

    public set interactEnabled(value:boolean) {
        if(this._interactEnabled != value) {
            this._interactEnabled = value;
            this.buttonMode = this._interactEnabled;
        }
    }

    constructor(arrowPrev?:PIXI.Sprite, arrowNext?:PIXI.Sprite, boxConfigs:IBoxConfig [] = []) {
        super();

        this._boxConfigs = boxConfigs;

        if(arrowPrev) {
            this._arrowPrevSprite = arrowPrev;
        }
        if(arrowNext) {
            this._arrowNextSprite = arrowNext;
        }
    }

    /**
     * To be able to call externally (e.g. from intro screen)
     */
    public forceInitAnimations():void {
        if(!this._animationsInited) {
            this.initAnimations();
        }
    }

    public static radians(degrees:number):number {
        return degrees * Math.PI / 180;
    }

    initAnimations() {
        if(!this._animationsInited) {
            this._boxWrapperSprite = new PIXI.Sprite();
            this._boxWrapperSprite.scale.set(0.75, 0.75);
            this._boxWrapperSprite.anchor.set(0.5, 0.5);

            this.createPages();
            this.createNextButton();
            this.createPreviousButton();
            this.initBaseInteractables();

            if (this.createFluff) {
                this.createFluff();

                if (this.initFluffInteractables) {
                    this.initFluffInteractables();
                }
            }

            this.addChild(this._boxWrapperSprite);
            this._animationsInited = true;
        }
    }

    protected getPageTransitionTimeline(fromPage:PIXI.Container, toPage:PIXI.Container):Timeline {
        const tl:TimelineLite = new TimelineLite();

        if (this._pageTransitionTimeline) {
            this._pageTransitionTimeline.pause();
            this._pageTransitionTimeline.kill();
        }
        if (this._pageFadeOutTween) {
            this._pageFadeOutTween.timeScale(5);
        }
        this._pageSprites.forEach((sprite) => {
            sprite.alpha = 0;
        });

        tl.add([
            this._pageFadeOutTween = TweenLite.to(fromPage, 1.6, {
                alpha: 0
            }),
            TweenLite.fromTo(toPage, 1.4, {
                alpha: 0
            }, {
                alpha: 1
            })
        ]);

        return this._pageTransitionTimeline = tl;
    }

    /**
     *
     *  On resize might be handled by owner of view
     */
    onResize(data:IResizeData) {
    }

    /**
     * Basic setup and positioning of pages and it's children, most likely to be
     * completely overriddn with game specific implementation.
     */
    protected createPages():void {

        this._boxConfigs.forEach((box, index) => {
                if(box.backgroundTexture) { // TODO: This specific impleementation only uses one bgTexture, and it's not really bg but vertical divider line
                    const lineSprite:PIXI.Sprite = new PIXI.Sprite(box.backgroundTexture);
                    lineSprite.anchor.set(0.5, 0.5);

                    lineSprite.x = 420;
                    lineSprite.y = 280;

                    lineSprite.scale.y = 1.2;

                    this._boxWrapperSprite.addChild(lineSprite);
                    this._lineDivider = lineSprite;
                }
                const contentWrapper:PIXI.Container = new PIXI.Container();

                    if (box.infoSprite) {
                        const info: PIXI.Sprite = box.infoSprite;
                        info.anchor.set(0.5, 0.5);
                        info.x = 55;
                        info.y = 320;

                        contentWrapper.addChild(info);
                    }

                    if(box.title) {
                    const title:PIXI.Text = new PIXI.Text(box.title, {
                        fill : 0xffffff,
                        fontFamily : "LuckiestGuy",
                        fontSize : 38,
                        wordWrap : true,
                        wordWrapWidth : 300,
                        padding: 20,
                        breakWords : true
                    });
                    title.anchor.set(0, 0);
                    title.x = 195;
                    title.y = 220;

                        if (index === 0 && !DragonTribe.api.gameClientConfiguration.showLowProbabilityGfx) {
                            title.y = 300;
                        }
                    this._titles.push(title);
                    contentWrapper.addChild(title);
                }

                if(box.text) {
                    const text:PIXI.Text = new PIXI.Text(box.text, {
                        fill : 0xffffff,
                        fontFamily : "Open Sans",
                        wordWrap: true,
                        padding: 20,
                        wordWrapWidth : 300,
                        fontSize : 26,
                        breakWords : true
                    });
                    text.anchor.set(0, 0);
                    text.x = 195;
                    text.y = (index === 1)?330:280;

                    this._paragraphs.push(text);
                    contentWrapper.addChild(text);
                }

                // This is what controls which content is being shown
                this._pageSprites.push(contentWrapper);

                this._boxWrapperSprite.addChild(contentWrapper);

                if(index !== 0) {
                    this._pageSprites[index].alpha = 0;
                }
        });
    }

    public setOrientationSetting(orientation:Orientation):void {
        if(orientation === Orientation.PORTRAIT) {
            if(this._lineDivider) {
                /*this._lineDivider.scale.set(1.2, 1.2);
                this._lineDivider.x = 310;
                this._lineDivider.y = 600;
                this._lineDivider.rotation = MathHelper.radians(90);*/

                //this._lineDivider.y = 295;
                //this._lineDivider.x = 690;
            }

            if(this._radioButtonWrapper) {
                //this._radioButtonWrapper.y = 270;
                //this._radioButtonWrapper.x = 730;
                /*this._radioButtonWrapper.scale.set(1.2, 1.2);
                this._radioButtonWrapper.x = 290;
                this._radioButtonWrapper.y = 680;
                this._radioButtonWrapper.rotation = MathHelper.radians(-90);*/
            }

            this._radioButtons.forEach((btn) => {
                btn.hitArea = new PIXI.Rectangle(btn.x - btn.width , btn.y - btn.height, btn.width * 2.5, btn.height * 2.5);
            });
        }
        else {

            this._radioButtons.forEach((btn) => {
                btn.hitArea = new PIXI.Rectangle(btn.x - btn.width , btn.y - btn.height, btn.width * 2.5, btn.height * 2.5);
            });
        }
    }

    /**
     * Any non generic visual stuff could be created from this point
     */
    protected createFluff():void {
        this.createRadioButtons("navDot", "navDotRing");
    }

    /**
     * Owls specific radio buttons
     */
    protected createRadioButtons(btnTextureName:string, btnFrameTextureName:string):void {
        const wrapperSprite:PIXI.Sprite = new PIXI.Sprite();
        wrapperSprite.anchor.set(0.5, 0.5);
        const progressBar:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("introProgressBar")[0]);
        progressBar.anchor.set(0.5,0.5);
        progressBar.position.set(220, 500 + this._progressBarYOffset);
        wrapperSprite.addChild(progressBar);

        this._pageSprites.forEach((sprite, index) => {
            const radioFrame:PIXI.Sprite = (btnFrameTextureName)? new PIXI.Sprite(GameResources.getTextures(btnFrameTextureName)[0]) : new PIXI.Sprite();
            const radioBtn:PIXI.Sprite = (btnTextureName)? new PIXI.Sprite(GameResources.getTextures(btnTextureName)[0]) : new PIXI.Sprite();

            radioBtn.anchor.set(0.5, 0.5);
            radioFrame.anchor.set(0.5, 0.5);
             radioFrame.y =497+ this._progressBarYOffset;
             radioFrame.x =220 - 62 +index*40;

            radioFrame.addChild(radioBtn);

            this._radioButtons.push(radioBtn);

            wrapperSprite.addChild(radioFrame);

            if(index !== 0) {
                radioBtn.alpha = 0.1;
            }
        });

        // Landscape positions
        wrapperSprite.y = 0;
        wrapperSprite.x = 0;


        this._boxWrapperSprite.addChild(wrapperSprite);
        this._radioButtonWrapper = wrapperSprite;
    }

    createNextButton():void {
        if(this._arrowNextSprite) {
            this._nextButton = this._arrowNextSprite;
            this._nextButton.anchor.set(0.5, 1);
            this._nextButton.x = 360;
            this._nextButton.y = 535+ this._progressBarYOffset;

            this._boxWrapperSprite.addChild(this._nextButton);
        }
    }

    createPreviousButton():void {
        if(this._arrowPrevSprite) {
            this._previousButton = this._arrowPrevSprite;
            this._previousButton.anchor.set(0.5, 1);
            this._previousButton.x = 84;
            this._previousButton.y = 535+ this._progressBarYOffset;

            this._boxWrapperSprite.addChild(this._previousButton);
        }
    }

    protected initBaseInteractables():void {

        if(this._arrowNextSprite) {
            this.initInteractive(this._arrowNextSprite, () => {
                this.slideShowEnabled = false;
                TweenMax.fromTo(this._arrowNextSprite.scale, 0.1, {
                    x: 1,
                    y: 1
                }, {
                    x:0.9,
                    y: 0.9,
                    yoyo: true,
                    repeat: 1
                });
                this.toggleNextPage();
            });
        }

        if(this._arrowPrevSprite) {
            this.initInteractive(this._arrowPrevSprite, () => {
                this.slideShowEnabled = false;
                TweenMax.fromTo(this._arrowPrevSprite.scale,0.1, {
                    x: 1,
                    y: 1
                }, {
                    x: 0.9,
                    y: 0.9,
                    yoyo: true,
                    repeat: 1
                });
                this.togglePreviousPage();
            });
        }
    }

    /**
     * Any non generic interactibles could be handled from this point
     */
    protected initFluffInteractables():void {
        this._radioButtons.forEach((sprite, index:number) => {
            if(index > -1) {
                this.initInteractive(sprite, () => {
                    if(index > -1) {
                        this.slideShowEnabled = false;
                        this.selectPage(index);
                    }
                })
            }
        });
    }

    initInteractive(sprite:PIXI.Sprite, clickCallback: (index?:number) => void = () => {}) {
        sprite.interactive = true;
        sprite.on('pointertap', clickCallback, this);
    }

    public toggleNextPage():void {
        if(this._interactEnabled) {
            const nextIndex:number = this._currentIndex + 1 > this._pageSprites.length - 1 ? this._currentIndex = 0 : this._currentIndex + 1;
            this.selectPage(nextIndex);
        }
    }

    public togglePreviousPage():void {
        if(this._interactEnabled) {
            const prevIndex:number = this._currentIndex - 1 < 0 ? this._currentIndex = this._pageSprites.length - 1 : this._currentIndex - 1;
            this.selectPage(prevIndex);
        }
    }

    protected visualSelectPage(fromPage:PIXI.Container, toPage:PIXI.Container) {
        if(this.getPageTransitionTimeline) {
            this.getPageTransitionTimeline(fromPage, toPage);
        }
        else {
            this._pageSprites.forEach((sprite) => {
                sprite.alpha = 0;
            });

            toPage.alpha = 1;
        }
    }

    public startSlideShow(slideInterval:number = 5):Timeline {
        const tl:TimelineLite = new TimelineMax({repeat: -1});

        if(this._slideShowTimeline != null && this._slideShowTimeline.isActive()) {
            this._slideShowTimeline.pause();
            this._slideShowTimeline.kill();
        }
        this._slideShowRunning = true;

        tl.add(() => {
            if(!this._slideShowEnabled && this._slideShowTimeline) {
                this.stopSlideShow();
            }
        });
        this._pageSprites.forEach(() => {
            tl.add(() => {this.toggleNextPage();}, `+=${slideInterval}`);
        });

        return this._slideShowTimeline = tl;
    }

    public stopSlideShow():void {
        if(this._slideShowTimeline && this._slideShowTimeline.isActive()) {
            this._slideShowTimeline.pause();
            this._slideShowTimeline.kill();
            this._slideShowRunning = false;
        }
    }

    public selectPage(pageIndex:number):void {
        if(this._interactEnabled) {
            if (pageIndex > -1 && pageIndex < this._pageSprites.length) {
                if(this._radioButtons) {
                    this._radioButtons.forEach((sprite) => {
                        sprite.alpha = 0.1;
                    });
                    this._radioButtons[pageIndex].alpha = 1;
                }

                //console.log("current index: " + this._currentIndex + "selected index: " + pageIndex);
                this.visualSelectPage(this._pageSprites[this._currentIndex], this._pageSprites[pageIndex]);
                this._currentIndex = pageIndex;
            }
        }
    }
}
